import { default as React, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs, useBreadcrumbs } from '../../../components/Breadcrumbs';
import { Button, ButtonType } from '../../../components/Button';
import { EmptyState } from '../../../components/EmptyState';
import { Icon } from '../../../components/Icon';
import { Loading, LoadingDots } from '../../../components/Loading';
import { Tile } from '../../../components/Tile';
import { usePrincipals } from '../../../hooks/queries';
import { useSharedDocuments } from '../../../hooks/useSharedDocuments';
import { getCurrentPrincipal } from '../../../lib/currentPrincipal';
import { Notification } from '../../../lib/notifications/notifications';
import { LatestActivity } from './LatestActivity';
import { SharedDocumentItem } from './SharedDocumentItem';

const forbiddenCharacters = /["*:<>?/\\|]/;

export const SharedDocuments = () => {
  const { t } = useTranslation();
  const { data: principals } = usePrincipals();
  const { currentPrincipal } = getCurrentPrincipal(principals);
  const maxResults = 4;

  enum SharepointError {
    NO_TEAM = 'noSharepointTeam',
    ACCESS_DENIED = 'accessDenied',
  }

  const [sharepointError, setSharepointError] =
    useState<SharepointError | null>(null);
  const {
    sortedItems,
    isLoading,
    sortBy,
    setSortBy,
    uploadFile,
    uploadProgress,
    latestActivity,
    isLatestActivityLoading,
    deleteFile,
  } = useSharedDocuments({
    maxResults,
    onUploadSuccess: () => {
      Notification({
        message: t('components:sharepoint:notifications:upload:success'),
        type: 'success',
        id: 'upload',
      });
    },
    onUploadError: () => {
      Notification({
        message: t('components:sharepoint:notifications:upload:error'),
        type: 'error',
        id: 'upload',
      });
    },
    onNoSharepointTeamError: () => {
      setSharepointError(SharepointError.NO_TEAM);
    },
    onLoadError: () => {
      Notification({
        message: t('components:sharepoint:notifications:load:error'),
        type: 'error',
        id: 'load',
      });
    },
    onAccessDeniedError: () => {
      setSharepointError(SharepointError.ACCESS_DENIED);
    },
    onDeleteSuccess: () => {
      Notification({
        message: t('components:sharepoint:notifications:delete:success'),
        type: 'success',
        id: 'delete',
      });
    },
    onDeleteError: () => {
      Notification({
        message: t('components:sharepoint:notifications:delete:error'),
        type: 'error',
        id: 'delete',
      });
    },
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }
    const [file] = files;
    if (forbiddenCharacters.test(file.name)) {
      Notification({
        message: t(
          'components:sharepoint:notifications:upload:invalidFileName',
        ),
        type: 'error',
        id: 'upload',
      });
      return;
    }
    if (file.name !== file.name.trim()) {
      Notification({
        message: t(
          'components:sharepoint:notifications:upload:invalidFileNameBlank',
        ),
        type: 'error',
        id: 'upload',
      });
      return;
    }
    uploadFile(file);
  };

  const breadcrumbs = useBreadcrumbs({
    label: t('components:sharepoint:root'),
    link: '',
  });

  return (
    <div>
      {sharepointError != null ? (
        <EmptyState
          hasBorder
          variant="market"
          message={t(
            `components:sharepoint:notifications:load:${sharepointError}`,
          )}
        />
      ) : (
        <div>
          <div className="mb-3">
            <span className="text-2xs font-sans uppercase tracking-widest">
              {t('components:sharepoint:latestActivity')}
            </span>
          </div>
          <div className="mb-5 grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
            {!isLatestActivityLoading
              ? latestActivity?.map((item, index) => (
                  <LatestActivity
                    key={index}
                    item={item}
                    currentPrincipal={currentPrincipal}
                  />
                ))
              : Array.from({ length: maxResults }).map((_, index) => (
                  <Tile key={index}>
                    <div className="flex h-8 w-full items-center justify-center">
                      <LoadingDots />
                    </div>
                  </Tile>
                ))}
          </div>
          <div className="flex w-full flex-row items-baseline justify-between">
            <div>
              <Breadcrumbs items={breadcrumbs} />
            </div>
            <div>
              <Button
                key="upload-button"
                buttonType={ButtonType.NORMAL}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadProgress !== null || isLoading}
                className="flex h-full flex-row items-center align-bottom"
              >
                <div>
                  <span className="mr-1 inline-block h-3 w-3">
                    <Icon
                      name={uploadProgress === null ? 'Add' : 'Loading'}
                      inline={true}
                    />
                  </span>
                  <span>
                    {uploadProgress === null
                      ? 'Upload'
                      : `Uploading (${uploadProgress}%)`}
                  </span>
                </div>
              </Button>
            </div>
          </div>
          <input
            title="file-upload"
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleChange}
          />
          <div className="border-grey-700 flex border-b-2 pb-4">
            <div className="xs:w-7/12 w-full md:px-5">
              <div
                className="flex cursor-pointer select-none font-semibold"
                id="sharepoint_sortNames"
                onClick={() => {
                  setSortBy({
                    field: 'name',
                    direction:
                      sortBy.field === 'name' && sortBy.direction === 'asc'
                        ? 'desc'
                        : 'asc',
                  });
                }}
              >
                {t('components:sharepoint:table:headers:filename')}
                <span className="ml-1 w-3 hover:text-blue-100">
                  <Icon
                    name={
                      sortBy.field === 'name' && sortBy.direction === 'desc'
                        ? 'ArrowNavUp'
                        : 'ArrowNavDown'
                    }
                    inline={true}
                    color={sortBy.field === 'name' ? undefined : 'grey-400'}
                    hoverColor="blue-100"
                  />
                </span>
              </div>
            </div>
            <div className="xs:w-2/12 w-full">
              <div
                id="sharepoint_sortModified"
                className="flex cursor-pointer select-none font-semibold"
                onClick={() => {
                  setSortBy({
                    field: 'lastModified',
                    direction:
                      sortBy.field === 'lastModified' &&
                      sortBy.direction === 'asc'
                        ? 'desc'
                        : 'asc',
                  });
                }}
              >
                {t('components:sharepoint:table:headers:lastModified')}
                <span className="ml-1 w-3">
                  <Icon
                    name={
                      sortBy.field === 'lastModified' &&
                      sortBy.direction === 'desc'
                        ? 'ArrowNavUp'
                        : 'ArrowNavDown'
                    }
                    inline={true}
                    color={
                      sortBy.field === 'lastModified' ? undefined : 'grey-400'
                    }
                    hoverColor="blue-100"
                  />
                </span>
              </div>
            </div>
            <div className="xs:w-2/12 w-full">
              <div
                id="sharpoint_sortOwner"
                className="flex cursor-pointer select-none font-semibold"
                onClick={() => {
                  setSortBy({
                    field: 'owner',
                    direction:
                      sortBy.field === 'owner' && sortBy.direction === 'asc'
                        ? 'desc'
                        : 'asc',
                  });
                }}
              >
                {t('components:sharepoint:table:headers:owner')}
                <span className="ml-1 w-3">
                  <Icon
                    name={
                      sortBy.field === 'owner' && sortBy.direction === 'desc'
                        ? 'ArrowNavUp'
                        : 'ArrowNavDown'
                    }
                    inline={true}
                    color={sortBy.field === 'owner' ? undefined : 'grey-400'}
                    hoverColor="blue-100"
                  />
                </span>
              </div>
            </div>
            <div className="xs:w-1/12 w-full"></div>
          </div>
          <div className="xs:flex-col xl:max-w-xl-content flex grow">
            {isLoading ? (
              <Loading
                className="mt-4 flex w-full items-center justify-center p-4"
                loading={isLoading}
              />
            ) : sortedItems.length > 0 ? (
              sortedItems.map((item) => (
                <SharedDocumentItem
                  key={item.id}
                  item={item}
                  itemDeleteCallback={() => deleteFile(item)}
                />
              ))
            ) : (
              <EmptyState
                variant="documents"
                message={t('components:sharepoint:emptyFolder')}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
